/**
 * Push Notification Context for Consumer Portal
 *
 * Wraps the usePushNotifications hook from design-system-ui and exposes
 * push notification state + actions via React context. Pages consume
 * `{ pushEnabled, requestPermission, isLoading, ... }` instead of each
 * wiring the full hook with tRPC mutations and Firebase config.
 *
 * @module contexts/PushNotificationContext
 */

import { type UsePushNotificationsReturn, usePushNotifications } from '@ops-matrix/design-system-ui';
import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';
import { env } from '../config/env';
import { useRegisterPushToken, useRevokePushToken } from '../hooks/useNotifications';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import { getFirebaseConfig } from '../utils/firebaseConfig';

// ============================================================================
// Types
// ============================================================================

interface PushNotificationContextType extends UsePushNotificationsReturn {
  /** Whether push notifications are currently enabled (permission granted) */
  pushEnabled: boolean;
}

// ============================================================================
// Context
// ============================================================================

const PushNotificationContext = createContext<PushNotificationContextType | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface PushNotificationProviderProps {
  children: ReactNode;
}

export function PushNotificationProvider({ children }: PushNotificationProviderProps) {
  const registerPushTokenMutation = useRegisterPushToken();
  const revokePushTokenMutation = useRevokePushToken();

  const onRegisterToken = useCallback(
    async (data: { fcmToken: string; deviceFingerprint: string; platform: string }) => {
      await registerPushTokenMutation.mutateAsync({
        fcmToken: data.fcmToken,
        deviceFingerprint: data.deviceFingerprint,
        platform: data.platform as 'web' | 'android' | 'ios',
      });
    },
    [registerPushTokenMutation],
  );

  const onRevokeToken = useCallback(
    async (data: { deviceFingerprint: string }) => {
      await revokePushTokenMutation.mutateAsync(data);
    },
    [revokePushTokenMutation],
  );

  const push = usePushNotifications({
    onRegisterToken,
    onRevokeToken,
    deviceFingerprint: getDeviceFingerprint(),
    firebaseConfig: getFirebaseConfig(),
    vapidKey: env.VITE_FIREBASE_VAPID_KEY ?? '',
  });

  const value = useMemo<PushNotificationContextType>(
    () => ({
      ...push,
      pushEnabled: push.permissionState === 'granted',
    }),
    [push],
  );

  return <PushNotificationContext.Provider value={value}>{children}</PushNotificationContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Access push notification state and actions from context.
 * Must be used within a PushNotificationProvider.
 */
export function usePushNotificationContext(): PushNotificationContextType {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotificationContext must be used within a PushNotificationProvider');
  }
  return context;
}
